import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Grid,
  IconButton,
  Drawer,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Button,
  Checkbox,
  Radio,
  RadioGroup,
  Alert,
  FormLabel,
  Rating,
  Slider,
  InputAdornment,
  Menu,
  ListItemIcon,
  CircularProgress,
  ListItemText,
  Tooltip,
} from "@mui/material"; // Removed Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AddIcon from "@mui/icons-material/Add";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";
import { LoadingButton } from "@mui/lab";
import MyQuill from "../quill/MyQuill";
import ParagraphDescription from "./ParagraphDescription";
import Labelinput from "./Labelinput";
import SelectDropDown from "./SelectDropdown";
import MyDatePicker from "./datetime/MyDatePicker";
import MyTimePicker from "./datetime/MyTimePicker";
import MyDateRange from "./datetime/MyDateRange";
import { useNavigate, useParams } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  X as TwitterIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import axios from "axios";
import MuPb from "../widgets/MuPb";
import { API_ENDPOINT, APP_ID } from "../utils/constants";
import { getAuthHeader, getErrorResponse, toastError } from "../utils/util";
import ProductCategoriesChecker from "./ProductCategoriesChecker";
import CountryStateCity from "./widgets/CountryStateCity";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { toast } from "react-toastify";
import useGetFormEdit from "../hooks/useGetFormEdit";
import SubmissionConfirmation from "./SubmissionConfirmation";
import { useDialog } from "../context/DialogProvider";
import { DIALOG_TYPES } from "../context/CommonDialog";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SelectedInputFields from "./SelectedInputFields";
import {
  Type,
  AlignLeft,
  FileText,
  CheckSquare,
  CircleDot,
  ChevronDownSquare,
  Calendar,
  Clock,
  CalendarRange,
  LayoutGrid,
  ToggleRight,
  Flag,
  Minus,
} from "lucide-react";

export const FIELD_TYPES = Object.freeze({
  TEXT_FIELD: "TextField",
  PLAIN_TEXT: "PlainText",
  PARAGRAPH_DESCRIPTION: "ParagraphDescription",
  CHECKBOX: "Checkbox",
  RADIO_GROUP: "RadioGroup",
  SELECT: "Select",
  DATE_PICKER: "DatePicker",
  TIME_PICKER: "TimePicker",
  DATE_RANGE: "DateRange",
  PRODUCT_CATEGORIES: "ProductCategories",
  SWITCH: "Switch",
  COUNTRY_STATE_CITY: "CountryStateCity",
  DIVIDER: "Divider",
  LABEL_INPUT: "LabelInput",
});

const getDefaultProperties = (type) => {
  switch (type) {
    case FIELD_TYPES.TEXT_FIELD:
      return {
        label: "Text Field",
        placeholder: "Enter text",
        types: INPUT_TYPES,
        type: "text",
      };

    case FIELD_TYPES.PLAIN_TEXT:
      return {
        label: "Plain Text",
        variant: "body1",
        value: "",
        options: [
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "subtitle1",
          "subtitle2",
          "body1",
          "body2",
          "caption",
          "button",
          "overline",
          "inherit",
        ],
      };

    case FIELD_TYPES.PARAGRAPH_DESCRIPTION:
      return {
        label: "Paragraph/Description",
        text: "Paragraph description here...",
        grid: "12",
      };

    case FIELD_TYPES.CHECKBOX:
      return {
        label: "Checkbox",
        grid: "12",
      };

    case FIELD_TYPES.LABEL_INPUT:
      return {
        label: "Label",
        grid: "12",
      };

    case FIELD_TYPES.RADIO_GROUP:
      return {
        label: "Options",
        options: ["Option 1", "Option 2"],
        grid: "12",
      };

    case FIELD_TYPES.SELECT:
      return {
        label: "Select",
        options: ["Option 1", "Option 2"],
        grid: "12",
      };

    case FIELD_TYPES.DATE_PICKER:
      return {
        label: "Date Picker",
        required: false,
      };

    case FIELD_TYPES.DATE_RANGE:
      return {
        label: "Date Range",
        required: false,
      };

    case FIELD_TYPES.TIME_PICKER:
      return {
        label: "Time Picker",
        required: false,
      };

    case FIELD_TYPES.PRODUCT_CATEGORIES:
      return {
        label: "Select Product Categories",
        desc: "Product categories will appear here",
        grid: "12",
      };

    case FIELD_TYPES.SWITCH:
      return {
        label: "Toggle Switch",
        grid: "12",
      };

    case FIELD_TYPES.COUNTRY_STATE_CITY:
      return {
        label: "Country / State / City",
        grid: "12",
      };

    case FIELD_TYPES.DIVIDER:
      return {
        label: "Divider",
      };

    default:
      return {
        grid: "12",
      };
  }
};

export const getIcon = (type, props = {}) => {
  switch (type.toLowerCase()) {
    case "x":
      return <TwitterIcon {...props} />;
    case "fb":
      return <FacebookIcon {...props} />;
    case "ln":
      return <LinkedInIcon {...props} />;
    case "in":
      return <InstagramIcon {...props} />;
    case "phone":
      return <PhoneIcon {...props} />;
    case "email":
      return <EmailIcon {...props} />;
    default:
      return null;
  }
};

export const INPUT_TYPES = [
  "text",
  "number",
  "password",
  "email",
  "search",
  "tel",
  "url",
  "date",
  "datetime-local",
  "month",
  "week",
  "time",
  "color",
];

export const AVAILABLE_FIELDS = [
  {
    id: FIELD_TYPES.TEXT_FIELD,
    type: FIELD_TYPES.TEXT_FIELD,
    label: "Text Field",
    icon: <Type size={20} />,
  },
  {
    id: FIELD_TYPES.PLAIN_TEXT,
    type: FIELD_TYPES.PLAIN_TEXT,
    label: "Plain Text",
    icon: <AlignLeft size={20} />,
  },
  {
    id: FIELD_TYPES.PARAGRAPH_DESCRIPTION,
    type: FIELD_TYPES.PARAGRAPH_DESCRIPTION,
    label: "Paragraph/Description",
    icon: <FileText size={20} />,
  },
  {
    id: FIELD_TYPES.CHECKBOX,
    type: FIELD_TYPES.CHECKBOX,
    label: "Checkbox",
    icon: <CheckSquare size={20} />,
  },
  {
    id: FIELD_TYPES.RADIO_GROUP,
    type: FIELD_TYPES.RADIO_GROUP,
    label: "Radio Group",
    icon: <CircleDot size={20} />,
  },
  {
    id: FIELD_TYPES.SELECT,
    type: FIELD_TYPES.SELECT,
    label: "Select",
    icon: <ChevronDownSquare size={20} />,
  },
  {
    id: FIELD_TYPES.DATE_PICKER,
    type: FIELD_TYPES.DATE_PICKER,
    label: "Date Picker",
    icon: <Calendar size={20} />,
  },
  {
    id: FIELD_TYPES.TIME_PICKER,
    type: FIELD_TYPES.TIME_PICKER,
    label: "Time Picker",
    icon: <Clock size={20} />,
  },
  {
    id: FIELD_TYPES.DATE_RANGE,
    type: FIELD_TYPES.DATE_RANGE,
    label: "Date Range",
    icon: <CalendarRange size={20} />,
  },
  {
    id: FIELD_TYPES.PRODUCT_CATEGORIES,
    type: FIELD_TYPES.PRODUCT_CATEGORIES,
    label: "Product Categories",
    icon: <LayoutGrid size={20} />,
  },
  {
    id: FIELD_TYPES.SWITCH,
    type: FIELD_TYPES.SWITCH,
    label: "Switch",
    icon: <ToggleRight size={20} />,
  },
  {
    id: FIELD_TYPES.COUNTRY_STATE_CITY,
    type: FIELD_TYPES.COUNTRY_STATE_CITY,
    label: "Country/State/City",
    icon: <Flag size={20} />,
  },
  {
    id: FIELD_TYPES.DIVIDER,
    type: FIELD_TYPES.DIVIDER,
    label: "Divider",
    icon: <Minus size={20} />,
  },
];

const RegistrationForm = () => {
  const { id } = useParams();
  const { showDialog } = useDialog();
  const navigate = useNavigate();
  const {
    data: formItems,
    isLoading: isFormLoading,
    isError: isFormError,
    error: formError,
    refetch,
  } = useGetFormEdit(id, true);

  const [availableFields, setAvailableFields] = useState(AVAILABLE_FIELDS);
  const [formFields, setFormFields] = useState([]);
  const [terms, setTerms] = useState();
  const [prevHeader, setPrevHeader] = useState();
  const [prevFooter, setPrevFooter] = useState();
  const [savingDraft, setSavingDraft] = useState(false);
  const [cloningForm, setCloningForm] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [file, setFile] = useState(null);

  const [activeStep, setActiveStep] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const steps = [
    "Create Form",
    "Terms & Conditions",
    "Submission Confirmation",
    "Choose Template",
  ];
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (formItems?.form) {
      setFormFields(formItems.form);
      setTerms(formItems.terms);
      setPrevHeader(formItems.prevHeader);
      setPrevFooter(formItems.prevFooter);
    }

    if (formItems?.eventName) {
      document.title = formItems.eventName;
    }
  }, [formItems]);
  const inputItemsField = useMemo(() => {
    return formFields.filter(
      (item) => !["PlainText", "Divider"].includes(item.type)
    );
  }, [formFields]);

  const handleSaveForm = useCallback(async () => {
    const seenLabels = new Set();
    for (const item of inputItemsField) {
      // Safely access the label, default to empty string if undefined
      const label = item.properties?.label?.trim() || "";

      if (label && seenLabels.has(label.toLowerCase())) {
        toast.error(
          `Duplicate field label found: "${item.properties.label}". Labels must be unique.`
        );
        return; // Stop execution
      }
      seenLabels.add(label.toLowerCase());
    }

    // --- 2. VALIDATION: Check for Email and Phone flags ---
    // Using .filter().length === 1 guarantees exactly ONE exists
    // (If you only care that AT LEAST one exists, use .some() instead)
    const emailFieldCount = inputItemsField.filter(
      (item) => item.isEmailField
    ).length;
    const phoneFieldCount = inputItemsField.filter(
      (item) => item.isPhoneField
    ).length;

    if (emailFieldCount !== 1) {
      toast.error("You must designate exactly one Email field.");
      return; // Stop execution
    }

    if (phoneFieldCount !== 1) {
      toast.error("You must designate exactly one Phone field.");
      return; // Stop execution
    }
    try {
      setSavingDraft(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}event/submit-draft`,
        {
          form: formFields,
          terms,
          prevHeader,
          prevFooter,
        },
        getAuthHeader(id)
      );
      toast.success(data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setSavingDraft(false);
    }
  }, [formFields, inputItemsField, terms, prevHeader, prevFooter, id]);

  const updatePhoneEmailSettings = useCallback((selectD) => {
    const { emailFieldId, phoneFieldId } = selectD;
    setFormFields((prevFields) =>
      prevFields.map((field) => ({
        ...field,
        // Evaluates to true ONLY if the id matches, otherwise false.
        // This automatically removes the flag from previously set items.
        isEmailField: field.id === emailFieldId,
        isPhoneField: field.id === phoneFieldId,
      }))
    );
  }, []); // Note: formFields can be removed from dependencies!

  const handleInputItemsClick = useCallback(() => {
    showDialog({
      type: DIALOG_TYPES.CUSTOM,
      disableEscapeKeyDown: true, // Prevent accidental closing if they are filling a form
      content: (
        <SelectedInputFields
          onDataSubmit={(data) => updatePhoneEmailSettings(data)}
          formFields={inputItemsField}
        />
      ),
    });
  }, [showDialog, inputItemsField]);

  const handleCloneFromVisitor = useCallback(async () => {
    try {
      setCloningForm(true);
      const response = await axios.post(
        `${API_ENDPOINT}event/clone-from-visitor`,
        {},
        getAuthHeader(id)
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setCloningForm(false);
    }
  }, [id, refetch]);

  // Updated to use showDialog from context
  const handleCloneFromVisitorClick = () => {
    showDialog({
      type: DIALOG_TYPES.CONFIRM_WARNING,
      title: "Clone from Visitor Form?",
      message:
        "This will overwrite the current Trade Delegates Registration form fields with a copy of the Visitor form. This action cannot be undone. Are you sure you want to continue?",
      buttonText: "Yes, Clone",
      onConfirm: handleCloneFromVisitor,
    });
  };

  if (isFormLoading) return <MuPb />;
  if (isFormError)
    return <Alert severity="error">{getErrorResponse(formError)}</Alert>;

  const handleFieldEdit = (fieldId) => {
    setSelectedFieldId(fieldId);
    setIsDrawerOpen(true);
  };

  const handleFieldDelete = (fieldId) => {
    setFormFields((prev) => prev.filter((f) => f.id !== fieldId));
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (destination.droppableId !== "formFields") return;

    if (source.droppableId === "availableFields") {
      const sourceField = availableFields[source.index];
      const newField = {
        ...sourceField,
        id: uuidv4(),
        properties: getDefaultProperties(sourceField.type),
      };
      const newFormFields = Array.from(formFields);
      newFormFields.splice(destination.index, 0, newField);
      setFormFields(newFormFields);
    } else {
      const reorderedFields = Array.from(formFields);
      const [movedField] = reorderedFields.splice(source.index, 1);
      reorderedFields.splice(destination.index, 0, movedField);
      setFormFields(reorderedFields);
    }
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleSubmit = () => {};

  const renderFormElement = (field) => {
    const { type, properties } = field;

    switch (type) {
      case FIELD_TYPES.TEXT_FIELD:
        return (
          <TextField
            type={properties.type}
            label={properties.label}
            placeholder={properties.placeholder}
            required={properties.required}
            multiline={properties.multiline}
            rows={properties.rows}
            InputProps={
              properties.iconed && properties.iconType
                ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        {getIcon(properties.iconType, {
                          fontSize: "small",
                          color: "action",
                        })}
                      </InputAdornment>
                    ),
                  }
                : null
            }
            fullWidth
            variant="outlined"
            size="small"
            disabled
          />
        );

      case FIELD_TYPES.PLAIN_TEXT:
        return (
          <Typography variant={properties.variant}>
            {properties.label}
          </Typography>
        );

      case FIELD_TYPES.CHECKBOX:
        return (
          <FormControlLabel
            control={<Checkbox />}
            disabled
            label={properties.label}
            required={properties.required}
          />
        );

      case FIELD_TYPES.DIVIDER:
        return <Divider />;

      case FIELD_TYPES.PARAGRAPH_DESCRIPTION:
        return <ParagraphDescription properties={properties} />;

      case FIELD_TYPES.LABEL_INPUT:
        return <Labelinput label={properties.label} />;

      case FIELD_TYPES.RADIO_GROUP:
        return (
          <FormControl component="fieldset">
            <Typography variant="subtitle1">{properties.label}</Typography>
            <RadioGroup row={properties.row}>
              {properties.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio disabled />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case FIELD_TYPES.SELECT:
        return <SelectDropDown properties={properties} />;

      case FIELD_TYPES.PRODUCT_CATEGORIES:
        return <Typography variant="h6">{properties.desc}</Typography>;

      case FIELD_TYPES.DATE_PICKER:
        return (
          <MyDatePicker
            min={properties.minDate}
            max={properties.maxDate}
            label={properties.label}
          />
        );

      case FIELD_TYPES.COUNTRY_STATE_CITY:
        return (
          <CountryStateCity
            onChange={({ country, state, city }) => {
              console.log("Selected Country:", country);
              console.log("Selected State:", state);
              console.log("Selected City:", city);
            }}
            gridContainerProps={{ spacing: 2 }}
            countryGridItemProps={{ xs: 12, md: 4 }}
            stateGridItemProps={{ xs: 12, md: 4 }}
            cityGridItemProps={{ xs: 12, md: 4 }}
            initialCountry={{ name: "India", code: "IN" }}
            countryRequired
            stateRequired
          />
        );

      case FIELD_TYPES.TIME_PICKER:
        return <MyTimePicker label={properties.label} />;

      case FIELD_TYPES.DATE_RANGE:
        return <MyDateRange label={properties.label} />;

      case FIELD_TYPES.SWITCH:
        return (
          <FormControlLabel
            control={
              <Switch
                checked={properties.active}
                defaultChecked={properties.active}
              />
            }
            label={properties.labelText || "Active"}
          />
        );

      default:
        return <Typography variant="body2">{type}</Typography>;
    }
  };

  const updateFieldProperties = (fieldId, newProperties) => {
    setFormFields((prevFields) =>
      prevFields.map((field) =>
        field.id === fieldId
          ? { ...field, properties: { ...field.properties, ...newProperties } }
          : field
      )
    );
  };

  const renderEditingPanel = () => {
    const field = formFields.find((f) => f.id === selectedFieldId);
    if (!field) return null;

    const { type, properties } = field;

    const handlValueChange = (propName, newV) => {
      updateFieldProperties(field.id, { [propName]: newV });
    };

    const handlePropertyChange = (propName) => (event) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;

      updateFieldProperties(field.id, { [propName]: value });
    };

    const handleOptionsChange = (index) => (event) => {
      const newOptions = [...properties.options];
      newOptions[index] = event.target.value;
      updateFieldProperties(field.id, { options: newOptions });
    };

    const addOption = () => {
      updateFieldProperties(field.id, {
        options: [...properties.options, ""],
      });
    };

    const removeOption = (index) => {
      updateFieldProperties(field.id, {
        options: properties.options.filter((_, i) => i !== index),
      });
    };

    const IsRequiredWidget = ({ label, id }) => (
      <FormControlLabel
        sx={{ ml: 0 }}
        control={
          <Switch
            size="small"
            checked={properties[id]}
            onChange={handlePropertyChange(id)}
          />
        }
        label={label}
      />
    );

    return (
      <Box p={2}>
        {field.isPhoneField && (
          <Alert severity="info">This is the phone number field</Alert>
        )}
        {field.isEmailField && (
          <Alert severity="info">This is the email field</Alert>
        )}
        <TextField
          size="small"
          label="Label"
          value={properties.label}
          onChange={handlePropertyChange("label")}
          fullWidth
          margin="normal"
        />

        <Box ml={1}>
          {field.isPhoneField && (
            <IsRequiredWidget
              label="Phone verification required?"
              id="isVerificationRequired"
            />
          )}
          {field.isEmailField && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IsRequiredWidget
                label="Disable public email?"
                id="disablePublicEmail"
              />

              <Tooltip
                arrow
                placement="right"
                title={
                  <React.Fragment>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 0.5 }}
                    >
                      Corporate Emails Only
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      If disabled, users are required to use their corporate
                      email address.
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      <strong>Blocked providers include:</strong>
                      <br />
                      • @gmail.com
                      <br />
                      • @yahoo.com
                      <br />
                      • @outlook.com
                      <br />
                      • @hotmail.com
                      <br />• @aol.com
                    </Typography>
                  </React.Fragment>
                }
              >
                <IconButton size="small" aria-label="info about public emails">
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <IsRequiredWidget label="Required?" id="required" />
        </Box>

        {type === FIELD_TYPES.PRODUCT_CATEGORIES && (
          <Box>
            <Typography gutterBottom variant="h6">
              Product Categories
            </Typography>
            <ProductCategoriesChecker id={id} />
          </Box>
        )}

        {type === FIELD_TYPES.PARAGRAPH_DESCRIPTION && (
          <Box mt={1}>
            <MyQuill
              value={properties.text}
              onChange={(newV) => handlValueChange("text", newV)}
            />
          </Box>
        )}

        {type === FIELD_TYPES.TEXT_FIELD && (
          <>
            <Stack my={2} direction="row" gap={1} ml={1}>
              <IsRequiredWidget label="Multiline?" id="multiline" />
              {properties.multiline && (
                <TextField
                  variant="outlined"
                  size="small"
                  type="number"
                  value={properties.rows}
                  label="Number of Rows"
                  onChange={handlePropertyChange("rows")}
                />
              )}
            </Stack>
            <Divider />
            <Stack my={2} direction="row" gap={1} ml={1}>
              <IsRequiredWidget label="Iconed?" id="iconed" />
              {properties.iconed && (
                <FormControl sx={{ my: 1 }} size="small" fullWidth>
                  <InputLabel>Select icon</InputLabel>
                  <Select
                    value={properties.iconType}
                    label="Select icon"
                    onChange={handlePropertyChange("iconType")}
                  >
                    {["x", "fb", "ln", "in", "phone", "email"].map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
            <FormControl sx={{ my: 1 }} size="small" fullWidth>
              <InputLabel>Select input type</InputLabel>
              <Select
                value={properties.type}
                label="Select input type"
                onChange={handlePropertyChange("type")}
              >
                {properties.types.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {type === FIELD_TYPES.PLAIN_TEXT && (
          <Box>
            <FormControl sx={{ my: 1 }} size="small" fullWidth>
              <InputLabel>Select variant</InputLabel>
              <Select
                value={properties.variant}
                label="Select variant"
                onChange={handlePropertyChange("variant")}
              >
                {properties.options.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              sx={{ mt: 1 }}
              fullWidth
              size="small"
              value={properties.value}
              label={properties.label}
              onChange={handlePropertyChange("value")}
            />
          </Box>
        )}

        {(type === FIELD_TYPES.RADIO_GROUP || type === FIELD_TYPES.SELECT) && (
          <>
            <Typography variant="body1">Options</Typography>
            {properties.options.map((option, index) => (
              <Box key={index} display="flex" alignItems="center" mb={1}>
                <TextField
                  size="small"
                  value={option}
                  onChange={handleOptionsChange(index)}
                  fullWidth
                />

                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => removeOption(index)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Box display="flex" justifyContent="flex-end">
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="outlined"
                onClick={addOption}
              >
                Add
              </Button>
            </Box>

            {type === FIELD_TYPES.RADIO_GROUP && (
              <IsRequiredWidget label="Is horizontal?" id="row" />
            )}

            {type === FIELD_TYPES.SELECT && (
              <Box border="1px solid #ddd" my={1} p={1} borderRadius={1}>
                <IsRequiredWidget label="Is auto select?" id="isAutoSelect" />

                <Alert severity="info">
                  Setting this option will enable search and select.
                </Alert>

                {properties.isAutoSelect && (
                  <>
                    <IsRequiredWidget
                      label="Is multi select?"
                      id="isMultiSelect"
                    />

                    <Alert severity="info">
                      Setting this option will enable selecting multiple
                      options.
                    </Alert>
                  </>
                )}
              </Box>
            )}
          </>
        )}

        {type === FIELD_TYPES.DATE_PICKER && (
          <Stack my={2} direction="row" gap={2}>
            <MyDatePicker
              label="Min Date"
              value={properties.minDate}
              onChange={(newV) => handlValueChange("minDate", newV)}
            />

            <MyDatePicker
              label="Max Date"
              value={properties.maxDate}
              onChange={(newV) => handlValueChange("maxDate", newV)}
            />
          </Stack>
        )}

        {type === FIELD_TYPES.SWITCH && (
          <Box my={1}>
            <TextField
              fullWidth
              sx={{ mb: 1 }}
              size="small"
              label="Switch Label"
              value={properties.labelText}
              onChange={handlePropertyChange("labelText")}
            />

            <IsRequiredWidget id="active" label="Is default state active?" />
          </Box>
        )}

        <TextField
          sx={{ mt: 2 }}
          size="small"
          fullWidth
          label="Grid Columns (1-12)"
          type="number"
          value={properties.grid || 12}
          onChange={handlePropertyChange("grid")}
          inputProps={{ min: 1, max: 12 }}
        />
        <Box display="flex" justifyContent="center">
          <LoadingButton
            sx={{ mt: 2 }}
            variant="outlined"
            size="small"
            onClick={() => setIsDrawerOpen(false)}
          >
            OK
          </LoadingButton>
        </Box>
      </Box>
    );
  };

  const getStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <>
            <DragDropContext onDragEnd={onDragEnd}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Droppable droppableId="availableFields" isDropDisabled>
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}>
                        <Typography variant="h6">Available Fields</Typography>
                        {availableFields.map((field, index) => (
                          <Draggable
                            key={field.id}
                            draggableId={field.id}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  padding: "8px",
                                  margin: "4px",
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: "4px",
                                  cursor: "grab",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {field.icon}
                                <Typography
                                  variant="body1"
                                  style={{ marginLeft: "8px" }}
                                >
                                  {field.label}
                                </Typography>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Typography fontSize={18}>
                    Editing{" "}
                    <i>
                      <code>{formItems?.formName}</code>
                    </i>{" "}
                    Form:{" "}
                    <code>
                      <strong>{formItems?.eventName}</strong>
                    </code>
                  </Typography>
                  <Box p={1} m={1} border={"1px solid #ccc"}>
                    <Droppable droppableId="formFields">
                      {(provided) => (
                        <Grid
                          container
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {formFields.length === 0 && (
                            <Typography
                              m={2}
                              variant="body1"
                              color="textSecondary"
                            >
                              Drag form fields here to build your form
                            </Typography>
                          )}

                          {formFields.map((field, index) => (
                            <Draggable
                              key={field.id}
                              draggableId={field.id}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <Grid
                                  item
                                  xs={
                                    field?.properties?.grid
                                      ? parseInt(field.properties.grid, 10)
                                      : 12
                                  }
                                >
                                  <Box
                                    m={1}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={provided.draggableProps.style}
                                  >
                                    <div
                                      {...provided.dragHandleProps}
                                      style={{
                                        padding: "8px",
                                        marginBottom: "8px",
                                        backgroundColor: snapshot.isDragging
                                          ? "#e0f7fa"
                                          : "#fff",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "grab",
                                      }}
                                    >
                                      <Box flex={1}>
                                        {renderFormElement(field)}
                                      </Box>

                                      <div
                                        style={{
                                          marginLeft: "auto",
                                          opacity: 0.7,
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleFieldEdit(field.id)
                                          }
                                          aria-label="edit"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleFieldDelete(field.id)
                                          }
                                          aria-label="delete"
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </div>
                                    </div>
                                  </Box>
                                </Grid>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Grid>
                      )}
                    </Droppable>
                  </Box>
                </Grid>
              </Grid>
            </DragDropContext>
            <Drawer
              anchor="right"
              open={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              sx={{ "& .MuiDrawer-paper": { width: 550 } }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1,
                  px: 2,
                  borderBottom: "1px solid #ddd",
                }}
              >
                <Typography variant="h6">Edit Selected field</Typography>
                <IconButton onClick={() => setIsDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              {renderEditingPanel()}
            </Drawer>
          </>
        );
      case 1:
        return (
          <>
            <Typography variant="h6" gutterBottom>
              Terms & Conditions
            </Typography>
            <Box mt={1}>
              <MyQuill value={terms} onChange={setTerms} />
            </Box>
          </>
        );
      case 2:
        return (
          <SubmissionConfirmation
            prevHeader={prevHeader}
            setPrevHeader={setPrevHeader}
            prevFooter={prevFooter}
            setPrevFooter={setPrevFooter}
            initialBadgeBg={formItems?.badgeBg}
            appId={id}
          />
        );
      case 3:
        return (
          <div>
            <Typography variant="h6" textAlign={"center"}>
              Choose Template
            </Typography>
            <Button
              onClick={() => {
                navigate(`/reg-template/${id}`);
              }}
              variant="outlined"
              startIcon={<SettingsOutlinedIcon />}
            >
              Click here to choose your template
            </Button>
          </div>
        );
      default:
        return "Unknown step";
    }
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file first.");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.post(
        `${API_ENDPOINT}event/upload-badge-bg`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            dbauth: APP_ID,
          },
        }
      );
      console.log("Upload success:", response.data);
      alert("File uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      alert("File upload failed.");
    }
  };

  return (
    <Container>
      <>
        <Typography textAlign="center" variant="h6" mb={2} mt={2}>
          Create a <code>{formItems?.formName}</code> Form for{" "}
          <code>
            <strong>{formItems?.eventName}</strong>
          </code>
        </Typography>

        <Stack
          mb={1}
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          {/* 1. Actions Menu Trigger */}
          <Button
            sx={{ py: 0.5 }}
            size="small"
            variant="outlined"
            onClick={handleMenuClick}
            endIcon={<KeyboardArrowDownIcon fontSize="small" />}
          >
            Form Settings
          </Button>

          {/* The Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate(`/reg-template/${id}`);
              }}
            >
              <ListItemIcon>
                <SettingsOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Customize template here</ListItemText>
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate(`/reg-email-template-editor/${id}`);
              }}
            >
              <ListItemIcon>
                <EmailIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Trade deleg. Email Editor</ListItemText>
            </MenuItem>

            {formItems?.formName === "Trade Delegates Registration" && (
              <>
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    navigate(`/reg-email-template-editor/${id}?type=si`);
                  }}
                >
                  <ListItemIcon>
                    <EmailIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Spl invitee Email Editor</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    navigate(`/reg-email-template-editor/${id}?type=tdVisitor`);
                  }}
                >
                  <ListItemIcon>
                    <EmailIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Trade del. to Visitor Email</ListItemText>
                </MenuItem>
                <MenuItem
                  disabled={cloningForm}
                  onClick={() => {
                    handleMenuClose();
                    handleCloneFromVisitorClick();
                  }}
                >
                  <ListItemIcon>
                    {cloningForm ? (
                      <CircularProgress size={20} />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText>Clone from visitor</ListItemText>
                </MenuItem>
              </>
            )}
          </Menu>

          {/* 2. Hides the spacer on mobile so buttons don't have a massive gap */}
          <Box flex={1} display={{ xs: "none", sm: "block" }} />

          {/* 3. Right Side Buttons */}
          <Button
            sx={{ py: 0.5 }}
            size="small"
            variant="outlined"
            onClick={handleInputItemsClick}
          >
            Input Items ({inputItemsField.length})
          </Button>

          <LoadingButton
            sx={{ py: 0.5 }}
            size="small"
            loading={savingDraft}
            variant="outlined"
            onClick={handleSaveForm}
            startIcon={<SaveOutlinedIcon fontSize="small" />}
          >
            Save Draft
          </LoadingButton>
        </Stack>
      </>

      <Stepper activeStep={activeStep}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box mt={4}>{getStepContent(activeStep)}</Box>
      <Box display={"flex"} justifyContent={"end"} mt={3}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          style={{ marginRight: "10px" }}
        >
          Back
        </Button>
        {activeStep < steps.length - 1 && (
          <Button variant="contained" color="primary" onClick={handleNext}>
            Next
          </Button>
        )}
        {activeStep === steps.length - 1 && (
          <LoadingButton
            variant="outlined"
            color="primary"
            onClick={handleSaveForm}
          >
            Submit
          </LoadingButton>
        )}
      </Box>
    </Container>
  );
};

export default RegistrationForm;
